import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { leadService, Lead, LeadPayload } from '../services/leadService';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import RequirePermission from '../components/common/RequirePermission';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useLeadFieldConfigs, useLeadStatusConfigs } from '../hooks/useLeadConfigs';
import ManageColumnsModal from '../components/modals/ManageColumnsModal';
import SaveViewModal from '../components/modals/SaveViewModal';
import DataTableToolbar from '../components/table/DataTableToolbar';
import DataTableContainer from '../components/table/DataTableContainer';
import DataTableBody from '../components/table/DataTableBody';
import MobileCardGrid from '../components/table/MobileCardGrid';
import TablePageLayout from '../components/table/TablePageLayout';
import useTablePage from '../components/table/hooks/useTablePage';
import { RowContextMenu, ContextMenuOption } from '../components/ui/RowContextMenu';
import LeadFormDialog from '../components/dialogs/LeadFormDialog';
import EmailSendDialog from '../components/dialogs/EmailSendDialog';
import SmsSendDialog from '../components/dialogs/SmsSendDialog';
import ResendQuestionnaireDialog from '../components/dialogs/ResendQuestionnaireDialog';
import LeadCommunicationsDialog from '../components/communications/LeadCommunicationsDialog';
import ComposeEmailDialog from '../components/communications/ComposeEmailDialog';
import { getApiError } from '../utils/apiError';
import { leadColumns } from '../config/leadColumns';
import { leadDisplayStatus, LEAD_STAGE_OPTIONS } from '../config/leadDisplayStatus';

// The Clalit districts a lead can belong to. Staff see every district at once,
// so this is a filter they narrow with - unlike the HMO coordinator, whose
// district comes from her own user record and is never offered as a choice.
const SUB_CLINIC_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All districts' },
  { value: 'דן', label: 'דן' },
  { value: 'דרום', label: 'דרום' },
];

const LeadsPage: React.FC = () => {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const userTimezone = useUserTimezone();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { fieldConfigs, dynamicColumns } = useLeadFieldConfigs();
  const { statusConfigs, statusLabel } = useLeadStatusConfigs();

  // Server-side pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  // Server-side status filter
  const [status, setStatus] = useState<string>('');
  const [subClinic, setSubClinic] = useState<string>('');

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false);
  const [commsOpen, setCommsOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [actionLead, setActionLead] = useState<Lead | null>(null);
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  // A failed save keeps the dialog open, so its error has to live inside the
  // dialog. Kept apart from `notice`, which reports things that happen while
  // the list is the thing on screen.
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset,
    formState: { errors, submitCount }, watch,
  } = useForm<any>();
  const watchedStatus = watch('status');

  const allColumns = useMemo(
    () => [...leadColumns, ...dynamicColumns],
    [dynamicColumns],
  );

  const getSortValue = useCallback((lead: Lead, key: string): any => {
    switch (key) {
      case 'createdAt': return lead.created_at;
      case 'subClinic': return (lead.sub_clinic || '').toLowerCase();
      case 'name': return `${lead.last_name} ${lead.first_name}`.toLowerCase();
      case 'idNumber': return lead.id_number;
      case 'email': return (lead.email || '').toLowerCase();
      case 'phone': return lead.phone;
      case 'status': return lead.status;
      case 'referral': return (lead.referral_name || '').toLowerCase();
      case 'questionnaireSent': return lead.questionnaire_sent_at;
      default:
        if (key.startsWith('df_')) return lead.additional_fields?.[key.slice(3)] ?? '';
        return '';
    }
  }, []);

  // Search runs on the server; these fields keep the client-side filter in
  // sortedData from dropping rows the server already matched.
  const getSearchText = useCallback((lead: Lead): string[] => {
    return [
      lead.first_name || '',
      lead.last_name || '',
      lead.id_number || '',
      lead.email || '',
      lead.phone || '',
      lead.sub_clinic || '',
      lead.referral_name || '',
      ...Object.values(lead.additional_fields || {}).map((v) => (v == null ? '' : String(v))),
    ];
  }, []);

  const table = useTablePage<Lead>({
    entityType: 'leads',
    columns: allColumns,
    tenantId: currentTenant?.id,
    getSortValue,
    getSearchText,
  });

  useEffect(() => {
    const fetchLeads = async () => {
      table.setLoading(true);
      table.setError(null);
      try {
        const response = await leadService.getLeads({
          skip: page * rowsPerPage,
          limit: rowsPerPage,
          search: table.searchQuery || undefined,
          status: status || undefined,
          // Narrowed on the server, not here: the table holds one page of a
          // district that runs to hundreds, so filtering on the client would
          // narrow the page and leave the total underneath contradicting it.
          sub_clinic: subClinic || undefined,
          sort_by: 'created_at',
          sort_order: 'desc',
        });
        table.setData(response.items);
        setTotal(response.total);
      } catch (err: unknown) {
        table.setError(getApiError(err, 'Failed to fetch leads'));
      } finally {
        table.setLoading(false);
      }
    };

    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, table.searchQuery, status, subClinic, reloadToken]);

  // Reset to the first page whenever the result set changes underneath
  useEffect(() => {
    setPage(0);
  }, [table.searchQuery, status]);

  const handleChangePage = (_event: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ---- form ----------------------------------------------------------------

  const openForm = (lead: Lead | null) => {
    setEditingLead(lead);
    setFormError(null);
    reset({
      first_name: lead?.first_name ?? '',
      last_name: lead?.last_name ?? '',
      email: lead?.email ?? '',
      phone: lead?.phone ?? '',
      id_number: lead?.id_number ?? '',
      status: lead?.status ?? '',
      additional_fields: lead?.additional_fields ?? {},
    });
    setFormOpen(true);
  };

  const onSubmit = handleSubmit(async (values: any) => {
    setFormError(null);
    // The dynamic fields come back off the form as strings; coerce them back to
    // what their config says they are, or a checkbox round-trips as "false".
    const additional: Record<string, any> = { ...(values.additional_fields ?? {}) };
    fieldConfigs.forEach((config) => {
      const raw = additional[config.key];
      if (raw === undefined) return;
      if (config.field_type === 'bool') {
        additional[config.key] = raw === '' || raw == null ? null : raw === true || raw === 'true';
      } else if (config.field_type === 'number') {
        additional[config.key] = raw === '' || raw == null ? null : Number(raw);
      }
    });

    const payload: LeadPayload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email || null,
      phone: values.phone || null,
      id_number: values.id_number || null,
      status: values.status || null,
      additional_fields: additional,
    };

    try {
      if (editingLead) await leadService.updateLead(editingLead.id, payload);
      else await leadService.createLead(payload);
      setFormOpen(false);
      setEditingLead(null);
      setReloadToken((t) => t + 1);
      setNotice({ severity: 'success', text: editingLead ? 'Lead updated' : 'Lead created' });
    } catch (err) {
      setFormError(getApiError(err, 'Failed to save lead'));
    }
  });

  // ---- row menu ------------------------------------------------------------

  const withSelectedLead = (fn: (lead: Lead) => void) => () => {
    const lead = table.selectedItem;
    table.handleMenuClose();
    if (lead) fn(lead);
  };

  const contextMenuOptions: ContextMenuOption[] = [
    { label: 'Edit', icon: '✏️', onClick: withSelectedLead(openForm) },
    {
      // tel: hands the number to whatever the coordinator already dials with -
      // her desk phone, her mobile, her softphone. The conference dialler the
      // patient screen uses needs a patient_id, which a lead does not have yet,
      // and buying one here would be a day of work to replace a link.
      label: 'Call',
      icon: '📞',
      onClick: withSelectedLead((lead) => {
        if (!lead.phone) {
          setNotice({ severity: 'error', text: 'This lead has no phone number' });
          return;
        }
        window.location.href = `tel:${lead.phone}`;
      }),
    },
    {
      label: 'Send Email Template',
      icon: '📧',
      onClick: withSelectedLead((lead) => { setActionLead(lead); setEmailOpen(true); }),
    },
    {
      label: 'Send Email',
      icon: '✉️',
      onClick: withSelectedLead((lead) => { setActionLead(lead); setComposeOpen(true); }),
    },
    {
      label: 'Send SMS',
      icon: '📱',
      onClick: withSelectedLead((lead) => { setActionLead(lead); setSmsOpen(true); }),
    },
    {
      label: 'Send Questionnaire',
      icon: '📋',
      onClick: withSelectedLead((lead) => { setActionLead(lead); setQuestionnaireOpen(true); }),
    },
    {
      label: 'Communications',
      icon: '💬',
      onClick: withSelectedLead((lead) => { setActionLead(lead); setCommsOpen(true); }),
    },
  ];

  // ---- rendering -----------------------------------------------------------

  const formatDynamic = (lead: Lead, columnKey: string): React.ReactNode => {
    const key = columnKey.slice(3);
    const value = lead.additional_fields?.[key];
    if (value === null || value === undefined || value === '') return '—';
    const config = fieldConfigs.find((c) => c.key === key);
    if (config?.field_type === 'bool') return value === true || value === 'true' ? 'Yes' : 'No';
    if (config?.field_type === 'date') return formatDate(String(value), userTimezone);
    if (config?.field_type === 'single_select') {
      return config.options?.find((o) => o.value === value)?.label ?? String(value);
    }
    return String(value);
  };

  const getColumnValue = (lead: Lead, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'createdAt': return formatDate(lead.created_at, userTimezone);
      case 'subClinic': return lead.sub_clinic || '-';
      case 'name': return `${lead.last_name} ${lead.first_name}`.trim();
      case 'idNumber': return lead.id_number || '-';
      case 'email': return lead.email || '-';
      case 'phone': return lead.phone || '-';
      case 'status':
        return (
          <Chip
            label={leadDisplayStatus(lead, statusLabel).label}
            color={leadDisplayStatus(lead, statusLabel).color}
            size="small"
          />
        );
      case 'questionnaireSent':
        return lead.questionnaire_sent_at
          ? formatDateTime(lead.questionnaire_sent_at, {}, userTimezone)
          : '-';
      case 'referral':
        return lead.referral_name || '—';
      case 'referralFile':
        return lead.referral_file_id
          ? <DescriptionIcon fontSize="small" color="action" />
          : '-';
      default:
        return columnKey.startsWith('df_') ? formatDynamic(lead, columnKey) : '';
    }
  };

  const renderMobileCard = (lead: Lead) => (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography variant="body2" color="text.secondary">
          {formatDate(lead.created_at, userTimezone)}
        </Typography>
        <Chip
          label={leadDisplayStatus(lead, statusLabel).label}
          color={leadDisplayStatus(lead, statusLabel).color}
          size="small"
        />
      </Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {`${lead.last_name} ${lead.first_name}`.trim()}
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {[lead.id_number, lead.phone, lead.email].filter(Boolean).join(' · ')}
      </Typography>
      {lead.sub_clinic && (
        <Typography variant="body2" color="text.secondary">
          {lead.sub_clinic}
          {lead.referral_file_id ? ' · referral doc attached' : ''}
        </Typography>
      )}
    </>
  );

  const actionLeadName = actionLead
    ? `${actionLead.first_name} ${actionLead.last_name}`.trim()
    : '';

  return (
  <RequirePermission check={!!user?.user_privilege?.leads} featureName="leads">
    <TablePageLayout loading={false} error={null}>
      <DataTableToolbar
        {...table.toolbarProps}
        title="Leads"
        searchPlaceholder="Search by name, ID, phone or email..."
        isAdmin={!!user?.user_privilege?.config}
        actions={
          <Box display="flex" gap={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => {
                  setStatus(e.target.value as string);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {LEAD_STAGE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>District</InputLabel>
              <Select
                value={subClinic}
                label="District"
                onChange={(e) => {
                  setSubClinic(e.target.value as string);
                  setPage(0);
                }}
              >
                {SUB_CLINIC_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" size="small" onClick={() => openForm(null)}>
              Add Lead
            </Button>
          </Box>
        }
      />

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      {table.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {table.error}
        </Alert>
      )}

      {table.loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : isMobile ? (
        <>
          <MobileCardGrid
            data={table.data}
            renderCard={renderMobileCard}
            getItemKey={(lead) => lead.id}
            emptyIcon={<AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />}
            emptyMessage="No leads found"
          />
          <TablePagination
            rowsPerPageOptions={[15, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      ) : (
        <DataTableContainer {...table.containerProps}>
          <DataTableBody
            data={table.data}
            {...table.tableBodyProps}
            getColumnValue={getColumnValue}
            onRowClick={table.handleRowClick}
            getRowKey={(lead) => lead.id}
          />
          <TablePagination
            rowsPerPageOptions={[15, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </DataTableContainer>
      )}

      <RowContextMenu {...table.menuProps} options={contextMenuOptions} />

      <LeadFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditingLead(null); setFormError(null); }}
        onSubmit={onSubmit}
        editingLead={editingLead}
        register={register}
        errors={errors}
        submitCount={submitCount}
        control={control}
        fieldConfigs={fieldConfigs}
        statusConfigs={statusConfigs}
        submitError={formError}
        watchStatus={watchedStatus}
      />

      {actionLead && (
        <>
          <EmailSendDialog
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            context="LEAD"
            leadId={actionLead.id}
            patientName={actionLeadName}
            patientEmail={actionLead.email}
            onSendSuccess={(text) => setNotice({ severity: 'success', text })}
            onSendError={(text) => setNotice({ severity: 'error', text })}
          />
          <SmsSendDialog
            open={smsOpen}
            onClose={() => setSmsOpen(false)}
            context="LEAD"
            leadId={actionLead.id}
            patientName={actionLeadName}
            patientPhone={actionLead.phone}
            onSendSuccess={(text) => setNotice({ severity: 'success', text })}
            onSendError={(text) => setNotice({ severity: 'error', text })}
          />
          <ComposeEmailDialog
            open={composeOpen}
            onClose={() => setComposeOpen(false)}
            entity="lead"
            patientId={actionLead.id}
            patientName={actionLeadName}
            patientEmail={actionLead.email}
            onSent={() => {
              setComposeOpen(false);
              setNotice({ severity: 'success', text: `Email sent to ${actionLeadName}` });
            }}
          />

          <LeadCommunicationsDialog
            open={commsOpen}
            onClose={() => setCommsOpen(false)}
            leadId={actionLead.id}
            leadName={actionLeadName}
            leadEmail={actionLead.email}
          />
          <ResendQuestionnaireDialog
            open={questionnaireOpen}
            onClose={() => setQuestionnaireOpen(false)}
            leadId={actionLead.id}
            leadName={actionLeadName}
            phone={actionLead.phone}
            onSent={(text) => {
              setNotice({ severity: 'success', text });
              setReloadToken((t) => t + 1);
            }}
          />

        </>
      )}

      <ManageColumnsModal {...table.manageColumnsProps} />
      <SaveViewModal {...table.saveViewModalProps} isAdmin={!!user?.user_privilege?.config} />
    </TablePageLayout>
  </RequirePermission>
  );
};

export default LeadsPage;
